import EventDelegation from '@jjwesterkamp/event-delegation'
import { show } from 'lambda-dom'
import { any, identity, not } from 'ramda'
import { BehaviorSubject, combineLatest, debounceTime, from, Subject } from 'rxjs'
import { map, pairwise, startWith, switchMap } from 'rxjs/operators'

import { setSubNavigationDelay, windowWidth$ } from '@app-bootstrappers'
import { articlePreviewManager, HeaderTheme, setHeaderDisplay, setHeaderTheme } from '@app-components'
import { DOMReadyP, showIf } from '@app-helpers'
import { autoSearch } from '@app-services'
import { DatasetElement } from '@app-types'
import {
    articleListContainer,
    articleListSpacer,
    filterBarButtons,
    filterLabels,
    heroContainer,
    heroFilterToggle,
    noResultsContainer,
    previewContainer,
    previewContentContainer,
} from './newsroom-overview.bootstrap'
import {
    addSearchTermToNoResultsContainer,
    hideFilterBar,
    hideFilters,
    initNoResultsClearForm,
    initSubscriptionForm,
    prettyPrintLabel,
    readUrlFilter,
    regenerateArticlesListAfterResize,
    showFilterBar,
    showFilters,
} from './newsroom-overview.lib'
import { ArticleFilter, QueryType } from './newsroom-overview.types'

setSubNavigationDelay(500)

// ------------------------------------------------------------------------------
//      Hero visibility observable
// ------------------------------------------------------------------------------
//
//      Derive an observable from an intersection-observer, looking for changes
//      in visibility thresholds for the page hero section. This is used for
//      determining the site-header theme.

const heroLargelyVisible$ = (() => {
    const subject = new Subject<boolean>()
    const observer = new IntersectionObserver(
        ([element]) => subject.next(element.intersectionRatio >= 0.85),
        { threshold: [1, 0.85, 0] },
    )

    observer.observe(heroContainer)
    return subject.asObservable()
})()

// ------------------------------------------------------------------------------
//      Hero button visibility observable
// ------------------------------------------------------------------------------
//
//      Derive an observable from an intersection-observer, looking for changes
//      in visibility for the filter-toggle in the hero section. This is used for
//      determining the filter-bar visibility while in non-filtered mode.

const heroFilterButtonVisible$ = (() => {
    const subject = new Subject<boolean>()
    const observer = new IntersectionObserver(
        ([entry]) => subject.next(entry.intersectionRatio === 1),
        { threshold: 1, rootMargin: '-70px 0px 0px' },
    )
    observer.observe(heroFilterToggle)
    return subject.asObservable()
})()

// ------------------------------------------------------------------------------
//      Filter state
// ------------------------------------------------------------------------------

/**
 * This is the user-engaged filter. This can be either an ArticleFilter, or null
 * when no active filter is set. The state-observable starts with the state that's
 * recorded in the browser URL at load-time.
 */
const activeFilter$ = new BehaviorSubject<ArticleFilter | null>(readUrlFilter())

/**
 * Shortcut observable that tells whether a user filter is currently active.
 */
const isFiltered$ = activeFilter$.pipe(map((articleFilter) => articleFilter !== null))

/**
 * There's an active filter, but it gives no search results
 */
const emptySearchResults$ = new BehaviorSubject(false)

// ------------------------------------------------------------------------------
//      Filter UI
// ------------------------------------------------------------------------------

/**
 * State observable emitting booleans that decide whether the filter menu
 * should be displayed.
 */
const showFilterMenu$ = new BehaviorSubject<boolean>(false)

/**
 * Observable for reading the UI label (text) for the current filter.
 */
const filterLabel$ = new Subject<string>()

// ------------------------------------------------------------------------------
//      Toggle hero visibility
// ------------------------------------------------------------------------------

isFiltered$.subscribe((isFiltered) => {
    showIf(! isFiltered, heroContainer)
    showIf(isFiltered, articleListSpacer)
})

// ------------------------------------------------------------------------------
//      Toggle the filter bar
// ------------------------------------------------------------------------------
//
// Conditions for display: EITHER - the hero button is no longer fully in view
//                             OR - the filter menu is shown
//                             OR - a UI filter is currently active

combineLatest([
    heroFilterButtonVisible$.pipe(map(not)),
    showFilterMenu$,
    isFiltered$,
])
    .pipe(
        // Map to pairs of emissions so that we can track which input stream
        // triggered the new emission by a value change.
        startWith([false, false, false]),
        pairwise(),

        map(([[xPrev, yPrev, zPrev], [x, y, z]]) => [

            // The first boolean tells whether to show the filter bar. If any
            // condition is true we must display the bar:
            x || y || z,

            // The second button tells whether to use CSS transitions for the
            // filter bar visibility change. That should only happen if the hero button
            // visibility input stream triggered the current emission. Other causes
            // should trigger a transition-less change of visibility.
            (xPrev !== x) && (yPrev === y) && (zPrev === z),
        ]),
    )
    .subscribe(([showBar, transition]) => (showBar
        ? showFilterBar(transition)
        : hideFilterBar(transition)))

// ------------------------------------------------------------------------------
//      Toggle the filter bar buttons
// ------------------------------------------------------------------------------

combineLatest([
    showFilterMenu$,
    isFiltered$,
    emptySearchResults$,
])
    .subscribe(([filterMenuOpen, isFiltered, emptySearchResults]) => {
        showIf(
            not(filterMenuOpen) && not(isFiltered),
            filterBarButtons.showMenuNotFiltered,
        )

        showIf(
            not(filterMenuOpen) && isFiltered && not(emptySearchResults),
            filterBarButtons.showMenuFiltered,
        )

        showIf(
            filterMenuOpen,
            filterBarButtons.closeMenu,
        )

        showIf(
            isFiltered && not(emptySearchResults),
            filterBarButtons.clearFilters,
        )

        showIf(
            isFiltered && emptySearchResults,
            filterBarButtons.resetProgress,
        )
    })

// ------------------------------------------------------------------------------
//      Populate the filter labels
// ------------------------------------------------------------------------------

filterLabel$.subscribe((label) => filterLabels.forEach((element) => {
    element.innerText = label

    if (element.parentElement && element.parentElement.classList.contains('nrfb--button')) {
        element.parentElement.classList.add('bordered')
    }
}))

// ------------------------------------------------------------------------------
//      Theme header
// ------------------------------------------------------------------------------
//
// Conditions for white: EITHER - the hero is scrolled out of view beyond threshold
//                           OR - the filter menu is shown
//                           OR - a UI filter is currently active

combineLatest([
    heroLargelyVisible$.pipe(map(not)),
    showFilterMenu$,
    isFiltered$,
])
    .pipe(
        map(any(identity)),
    )
    .subscribe((fillHeader) => {
        if (fillHeader) {
            setHeaderDisplay('brand-logo')
            setHeaderTheme(HeaderTheme.WHITE)
        } else {
            setHeaderDisplay('none')
            setHeaderTheme(HeaderTheme.TRANSPARENT_WHITE)
        }
    })

// ------------------------------------------------------------------------------
//      Filter toggles
// ------------------------------------------------------------------------------
//
//      Feed the `showFilterMenu$` state-hub in response to UI interaction,
//      and subscribe the Lib functions to show / hide the menu when the
//      state-hub emits.

EventDelegation
    .global()
    .events('click')
    .select('[data-open-filters]')
    .listen(() => showFilterMenu$.next(true))

EventDelegation
    .global()
    .events('click')
    .select('[data-close-filters]')
    .listen(() => showFilterMenu$.next(false))

showFilterMenu$.subscribe((doShow) => {
    if (doShow) {
        showFilters()
    } else {
        hideFilters()
    }
})

const toggleTagFilter = (tag: string) => {
    const selectedTagsElement = document.querySelector<HTMLInputElement>('input[name="tag"]')
    if (! selectedTagsElement) {
        return
    }

    const activeFilter: ArticleFilter = readUrlFilter() || { qt: QueryType.TAG, qv: '' }
    const selectedTags = selectedTagsElement.value.split(',').filter((t) => !! t)

    // Add/remove tag to/from selectedTags
    if (selectedTags.includes(tag)) {
        selectedTags.splice(selectedTags.indexOf(tag), 1)
    } else {
        selectedTags.push(tag)
    }

    selectedTagsElement.value = selectedTags.join(',')
    activeFilter.qv = selectedTagsElement.value

    activeFilter$.next(activeFilter)
    filterLabel$.next(prettyPrintLabel(activeFilter.qv))
}

EventDelegation
    .global()
    .events('click')
    .select('[data-multi-select] .tag-list__tag')
    .listen((e) => {
        e.preventDefault()
        const tagElement = e.target as HTMLElement
        const tag = tagElement?.dataset?.value || ''

        tagElement.classList.toggle('tag-list__tag--selected')
        toggleTagFilter(tag)
    })

// ------------------------------------------------------------------------------
//      Reset search values
// ------------------------------------------------------------------------------
//
//      2 buttons that reset the Newsroom by navigating you to a blank URL
//

// eslint-disable-next-line no-lone-blocks
{
    // --------------------------------------------------------------------------
    //      1 / 2 - from clear-filter triggers
    // --------------------------------------------------------------------------

    EventDelegation
        .global()
        .events('click')
        .select('[data-clear-filters]')
        .listen(() => {
            document.location.href = window.location.href.split('?')[0]
        })

    // --------------------------------------------------------------------------
    //      2 / 2 - from close filters button in no results
    // --------------------------------------------------------------------------

    EventDelegation
        .global()
        .events('click')
        .select('[data-reset-progress]')
        .listen(() => {
            document.location.href = window.location.href.split('?')[0]
        })
}

activeFilter$.subscribe((articleFilter: ArticleFilter | null) => {
    if (articleFilter) {
        filterLabel$.next(articleFilter.qv)
    }
})

// ------------------------------------------------------------------------------
//      Article Management
// ------------------------------------------------------------------------------

const previewManager = articlePreviewManager(previewContainer, previewContentContainer)

EventDelegation
    .global()
    .events('click')
    .select<DatasetElement<{ preview: string }>>('[data-preview]')
    .listen((e) => {
        e.preventDefault()

        const target = e.target as HTMLElement

        if (target.classList.contains('article-card__clap')
            || target.closest('.article-card__clap')) return

        previewManager.loadPreview(parseInt(e.delegator.dataset.preview, 10)).then()
    })

EventDelegation
    .global()
    .events('click')
    .select('[data-clear-preview]')
    .listen(previewManager.clearPreview)

DOMReadyP.then(() => {
    const activeFilter: ArticleFilter | null = readUrlFilter()

    if (activeFilter) {
        activeFilter$.next(activeFilter)
        filterLabel$.next(prettyPrintLabel(activeFilter.qv))
    }

    windowWidth$.pipe(
        debounceTime(500),
        switchMap(() => from(regenerateArticlesListAfterResize())),
    ).subscribe({
        next: () => {
            autoSearch()
            initSubscriptionForm()

            if (document.querySelectorAll('.article-card').length < 1 && activeFilter) {
                articleListContainer.style.paddingTop = '0'
                articleListContainer.style.marginTop = '3.75rem'
                show(noResultsContainer)
                initNoResultsClearForm()
                addSearchTermToNoResultsContainer(prettyPrintLabel(activeFilter.qv))
                emptySearchResults$.next(true)
            } else {
                emptySearchResults$.next(false)
            }
        },
    })
})
